export type FieldErrors = Record<string, string>;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Per field problems from the server, keyed like the form ("name", "latitude"). */
    readonly fields?: FieldErrors,
    /** Anything else the server sent, for example a list of `problems` that block publishing. */
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
  }
}

/** Calls the admin API with the signed-in cookie. Errors carry the server's readable message. */
export async function api<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: options.method ?? 'GET',
      credentials: 'same-origin',
      // A FormData body (file uploads) sets its own content type.
      headers:
        options.body === undefined || options.body instanceof FormData
          ? undefined
          : { 'Content-Type': 'application/json' },
      body:
        options.body === undefined || options.body instanceof FormData
          ? (options.body as FormData | undefined)
          : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.', 0);
  }
  const data = (await res.json().catch(() => null)) as
    (T & { error?: string; fields?: FieldErrors }) | null;
  if (!res.ok) {
    throw new ApiError(
      data?.error ?? 'Something went wrong. Try again.',
      res.status,
      data?.fields,
      data ?? undefined,
    );
  }
  return data as T;
}
